import { useState, useEffect } from 'react';
import { settingsApi } from '../../api/settings';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import EmailProviderSettings from '../../components/settings/EmailProviderSettings';
import ApplicationSettings from '../../components/settings/ApplicationSettings';
import EmailTemplateSettings from '../../components/settings/EmailTemplateSettings';
import TestEmailProviderModal from '../../components/settings/TestEmailProviderModal';
import Users from '../settings/Users';
import UserSignatures from '../settings/UserSignatures';
import { useUIStore } from '../../stores/uiStore';

/**
 * Settings Page
 * Admin-only page for configuring system settings
 */
export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testProvider, setTestProvider] = useState(null);
  const [activeTab, setActiveTab] = useState('email');
  const [connectionStatus, setConnectionStatus] = useState('untested');
  const [connectionError, setConnectionError] = useState(null);
  const [showSaveAnywayModal, setShowSaveAnywayModal] = useState(false);
  const [pendingValidation, setPendingValidation] = useState(null);
  
  const { showToast } = useUIStore();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsApi.getAll();
      setSettings(response.data);
    } catch (error) {
      showToast('Failed to load settings', 'error');
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (skipValidation = false) => {
    try {
      setSaving(true);
      setConnectionStatus('testing');
      setConnectionError(null);
      
      // Save with validation enabled (unless explicitly skipped)
      const response = await settingsApi.update(settings, !skipValidation);
      
      // Check validation result
      if (response.data.validation && !response.data.validation.success) {
        // Validation failed
        setConnectionStatus('failed');
        setConnectionError(response.data.validation.error);
        setPendingValidation(response.data.validation);
        
        // Ask user if they want to save anyway
        if (!skipValidation) {
          setShowSaveAnywayModal(true);
          showToast('Connection test failed. Review the error details.', 'error');
          return;
        }
      } else if (response.data.validation?.success) {
        // Validation succeeded
        setConnectionStatus('connected');
        setConnectionError(null);
        showToast('Settings saved and connection verified ✓', 'success');
      } else {
        // No validation (e.g., non-SMTP settings)
        setConnectionStatus('saved-untested');
        showToast('Settings saved successfully', 'success');
      }
      
      loadSettings(); // Reload to get masked passwords
    } catch (error) {
      setConnectionStatus('failed');
      showToast('Failed to save settings', 'error');
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAnyway = async () => {
    setShowSaveAnywayModal(false);
    await handleSave(true); // Save without validation
  };

  const handleReset = async () => {
    if (!confirm('Are you sure you want to reset all settings to defaults?')) return;
    
    try {
      await settingsApi.resetDefaults();
      showToast('Settings reset to defaults', 'success');
      loadSettings();
    } catch (error) {
      showToast('Failed to reset settings', 'error');
      console.error('Error resetting settings:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const handleTestEmail = (provider) => {
    setTestProvider(provider);
    setShowTestModal(true);
  };

  const tabs = [
    { id: 'email', label: 'Email Configuration' },
    { id: 'application', label: 'Application Settings' },
    { id: 'email_templates', label: 'Email Templates' },
    { id: 'signatures', label: 'My Signatures' },
    { id: 'users', label: 'User Management' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure system settings and email provider credentials
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={handleReset}
          >
            Reset to Defaults
          </Button>
          <Button
            onClick={handleSave}
            loading={saving}
          >
            Save Changes
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <Card>
        {activeTab === 'email' && (
          <EmailProviderSettings
            settings={settings}
            onChange={setSettings}
            onTest={handleTestEmail}
            connectionStatus={connectionStatus}
            connectionError={connectionError}
            onStatusChange={(status, error) => {
              setConnectionStatus(status);
              setConnectionError(error);
            }}
          />
        )}
        {activeTab === 'application' && (
          <ApplicationSettings
            settings={settings?.application || {}}
            onChange={(application) => setSettings({ ...settings, application })}
          />
        )}
        {activeTab === 'email_templates' && (
          <EmailTemplateSettings
            settings={settings?.email_templates || {}}
            onChange={(email_templates) => setSettings({ ...settings, email_templates })}
          />
        )}
        {activeTab === 'signatures' && (
          <UserSignatures />
        )}
        {activeTab === 'users' && (
          <Users />
        )}
      </Card>

      {/* Test Email Provider Modal */}
      {showTestModal && testProvider && (
        <TestEmailProviderModal
          provider={testProvider}
          onClose={() => {
            setShowTestModal(false);
            setTestProvider(null);
          }}
        />
      )}
    </div>
  );
}